/* eslint-disable no-async-promise-executor */
import socketService, { SocketService } from './socketService';
import env from 'env';
import { safeParse, safeStringify, sentryError } from '../../utils';
import api from '../../api';
import credentialsService from 'services/credentialsService';
import { FIVE_SECONDS, MINUTE } from 'data/global';

const MAX_RECONNECT_TIME = 30_000;
// Below this, a still-open socket after a quick tab switch is trusted as-is.
// Above it, we can't trust readyState (a slept/backgrounded socket can report
// OPEN while being a dead "zombie"), so we force a fresh connection.
const STALE_THRESHOLD = MINUTE;

let reconnectTimeout: NodeJS.Timeout;
let reconnectTime = FIVE_SECONDS;

// Bumped on every connect attempt. Each socket captures the generation it was
// created in and ignores its own events once a newer attempt supersedes it.
// Without this, the old socket's clean close during a reconnect's token fetch
// (this.socket isn't swapped until the new socket is built) fires the close
// handler and spawns a second, parallel connection.
let generation = 0;

// True from the start of a connect attempt until its socket opens or fails, so
// the wake handlers skip redundant reconnects while one is already in flight
// (online + focus + visibilitychange can all fire at once on wake).
let isConnecting = false;

function scheduleReconnect(this: SocketService) {
  if (!this.shouldReconnect || this.isClosed) return;

  clearTimeout(reconnectTimeout);
  reconnectTimeout = setTimeout(() => {
    if (this.isClosed) return;
    reconnectTime = Math.min(reconnectTime + FIVE_SECONDS, MAX_RECONNECT_TIME);
    this.connect();
  }, reconnectTime);
}

function connect(this: SocketService) {
  // No auth token — e.g. logged out in another tab (cookies are shared across
  // tabs, but this tab's socket state isn't, so a wake event can still reach
  // here). Firing getToken() now would hit the BE with no Authorization header
  // ("bearer token missing"). Stop the loop instead of retrying.
  if (!credentialsService.token) {
    this.isClosed = true;
    // Close any lingering socket ourselves rather than trusting the caller to
    // have done so. isClosed is set first, so this socket's close handler bails
    // at its own isClosed check and won't spawn a reconnect.
    this.socket?.close();
    isConnecting = false;
    clearTimeout(reconnectTimeout);
    return Promise.resolve();
  }

  // Claim a generation for this attempt synchronously, before the async token
  // fetch, so overlapping callers and stale sockets can tell they've been
  // superseded.
  const myGen = ++generation;
  isConnecting = true;

  // Close any previous socket up front so a superseded connection is never left
  // open (whether the caller used reconnect() or connect() directly). Its close
  // event is ignored below via the stale-generation guard, so this can't loop.
  this.socket?.close();

  return new Promise(async (resolve, reject) => {
    this.isClosed = false;

    clearTimeout(reconnectTimeout);

    let token: string;
    try {
      const { data } = await api.websocket.getToken();
      token = data.token;
    } catch (err) {
      if (myGen !== generation) return; // superseded — a newer attempt owns recovery
      isConnecting = false;
      (err as any).errorType = 'Websocket token error';
      sentryError(err);
      // Recover instead of killing the reconnect loop: a failed token fetch
      // happens before the socket (and its close handler) exist, so schedule a
      // retry ourselves. Don't reject — call sites are fire-and-forget.
      scheduleReconnect.call(this);
      return;
    }

    // A newer connect, or a disconnect (e.g. logout), landed while we were
    // fetching the token — abandon this attempt so we can't open a duplicate.
    if (myGen !== generation) return;
    if (this.isClosed) {
      isConnecting = false;
      return;
    }

    const socket = new WebSocket(`${env.WS_SERVER_ENDPOINT}?at=${token}`);
    this.socket = socket;

    socket.addEventListener('message', (ev) => {
      const event = safeParse(ev.data);

      const { eventType, eventData } = event;

      if (!eventType) {
        sentryError(
          new Error(
            `Received socket event without type: ${safeStringify(event)}`,
          ),
        );

        return;
      }

      this.consumers[eventType]?.forEach((cb) => cb(eventData));
    });

    socket.addEventListener('close', (ev) => {
      // A newer attempt already replaced this socket — ignore its events so a
      // stale close can't spawn a duplicate connection.
      if (myGen !== generation) return;
      isConnecting = false;

      if (this.isClosed) return;

      if (!this.shouldReconnect) {
        (ev as any).errorType = 'Websocket close error';
        sentryError(ev);
        return reject(ev.reason);
      }

      if (ev.wasClean) {
        this.connect();
      } else {
        // If not clean wait for reconnectTime
        scheduleReconnect.call(this);
      }
    });

    socket.addEventListener('open', () => {
      if (myGen !== generation) {
        socket.close(); // superseded before opening — drop the orphan socket
        return;
      }
      isConnecting = false;
      reconnectTime = FIVE_SECONDS; // reset backoff after a healthy connection
      resolve('');
    });
  });
}

const resolveSocketService = socketService(connect);

// When the tab was last hidden or the window last lost focus. Used to tell a
// quick switch-away (trust the socket) from a long absence such as an overnight
// laptop sleep (an "OPEN" readyState may be a dead zombie — force a reconnect).
let awayAt = Date.now();

function ensureConnection() {
  if (resolveSocketService.isClosed) return; // intentional disconnect (logged out)
  if (isConnecting) return; // an attempt is already in flight (incl. its token fetch)

  const state = resolveSocketService.socket?.readyState;

  // A connect is already in flight — let it finish rather than restart it.
  if (state === WebSocket.CONNECTING) return;

  // Still open after only a brief absence: trust it, no need to churn.
  if (state === WebSocket.OPEN && Date.now() - awayAt < STALE_THRESHOLD) return;

  // Otherwise force a fresh socket: it's closed/closing, or it was away long
  // enough that an "OPEN" readyState may be a dead zombie connection.
  resolveSocketService.reconnect();
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      awayAt = Date.now(); // tab hidden — start counting the absence
      return;
    }
    ensureConnection();
  });
}

if (typeof window !== 'undefined') {
  // Window lost focus (e.g. switched apps) — remember when, so a quick return
  // is still trusted by the threshold in ensureConnection.
  window.addEventListener('blur', () => (awayAt = Date.now()));
  window.addEventListener('focus', ensureConnection);
  // Network came back (e.g. laptop woke, closed lid reopened) — a socket that
  // still reports OPEN after a long idle is very likely dead.
  window.addEventListener('online', ensureConnection);
}

export default resolveSocketService;
